import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Settings, Maximize, Minimize } from 'lucide-react';

import { Modal, Button, IconButton } from '../../../shared/ui';

import { useFormFactor } from '../../../utils/formFactor';
import { useBreakpoint } from '../../../hooks/useBreakpoint';
import { useAppSelector } from '../../../hooks/redux';
import { selectUser } from '../../../store/auth.slice';
import { Breakpoint } from '../../../utils/breakpoint';

import { ILifeItem } from './types';
import { WheelOfBalance } from './WheelOfBalance';
import { buildShortTitle, DEFAULT_LIFE_ITEMS, DEFAULT_SECTOR_NAMES, sectorColor, WHEEL_SECTOR_COUNT } from './defaults';
import {
  areWheelConfigsEqual,
  clampSectorCount,
  MAX_SECTOR_NAME_LENGTH,
  MIN_WHEEL_SECTOR_COUNT,
  normalizeSectorNames,
  normalizeWheelConfig,
  normalizeWheelValues,
  type WheelConfig,
} from './wheel-config';
import { WheelSettingsPanel } from './WheelSettingsPanel';

const CONFIG_SYNC_THROTTLE_MS = 100;

export interface IWheelPanel {
  isClient?: boolean;
  withTips?: boolean;
  small?: boolean;
  inJournal?: boolean;
  values: number[];
  /** Controlled sector titles. When omitted, panel keeps local names. */
  sectorNames?: string[];
  setValues?: (ar: number[]) => void;
  /** Allows the client to change scores after the psychologist explicitly unlocks selection. */
  selectionUnlocked?: boolean;
  /** Status shown over a locked client wheel. */
  clientStatusMessage?: string;
  /** Fired when psychologist changes structure/titles (incl. live settings preview). */
  onConfigChange?: (config: WheelConfig) => void;
  setReady?: () => void;
  onButtonTips?: () => void;
  showClientSelectingOverlay?: boolean;
  /** When false, psychologist cannot open wheel structure settings. */
  allowSettings?: boolean;
  /** Notifies parent when the settings drawer opens/closes. */
  onSettingsVisibilityChange?: (open: boolean) => void;
  /** Notifies parent when fullscreen zoom opens/closes. */
  onFullscreenChange?: (open: boolean) => void;
}

function getContrastColorForHex(hex: string) {
  const c = hex.substring(1);
  const rgb = parseInt(c, 16);
  const r = (rgb >> 16) & 0xff;
  const g = (rgb >> 8) & 0xff;
  const b = rgb & 0xff;
  const luma = 0.299 * r + 0.587 * g + 0.114 * b;
  return luma > 186 ? '#000' : '#fff';
}

function buildLifeItemsFromNames(names: string[]): ILifeItem[] {
  return names.map((name, i) => {
    const title = (names[i] ?? DEFAULT_SECTOR_NAMES[i] ?? `Сектор ${i + 1}`).trim() || DEFAULT_SECTOR_NAMES[i];
    const base = DEFAULT_LIFE_ITEMS[i];
    return {
      color: base?.color ?? sectorColor(i),
      title,
      shortTitle: base?.shortTitle && title === base.title ? base.shortTitle : buildShortTitle(title),
    };
  });
}

export const WheelPanel = ({
  isClient = false,
  withTips = false,
  small = false,
  inJournal = false,
  values,
  sectorNames: sectorNamesProp,
  setValues,
  selectionUnlocked = true,
  clientStatusMessage,
  onConfigChange,
  setReady,
  onButtonTips,
  showClientSelectingOverlay = false,
  allowSettings = true,
  onSettingsVisibilityChange,
  onFullscreenChange,
}: IWheelPanel) => {
  const userData = useAppSelector(selectUser);
  const [sectorNames, setSectorNames] = useState<string[]>(() => {
    const count = sectorNamesProp?.length || values.length || DEFAULT_SECTOR_NAMES.length;
    return normalizeSectorNames(sectorNamesProp ?? DEFAULT_SECTOR_NAMES, count);
  });
  /** Local values when parent does not pass setValues (psy view). Cleared when props.values change. */
  const [localValues, setLocalValues] = useState<number[] | null>(null);

  const [modalShow, setModalShow] = useState(true);
  const [editMode, setEditMode] = useState(isClient && selectionUnlocked && setValues !== undefined);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const formFactor = useFormFactor();
  const shouldShowClientOverlay = Boolean(showClientSelectingOverlay && !isClient && !inJournal);
  const shouldShowClientStatus = Boolean(isClient && !selectionUnlocked && clientStatusMessage && !inJournal);

  const [showSettings, setShowSettings] = useState(false);
  const [tempCount, setTempCount] = useState(WHEEL_SECTOR_COUNT);
  const [tempNames, setTempNames] = useState<string[]>([...DEFAULT_SECTOR_NAMES]);
  const [tempValues, setTempValues] = useState<number[]>(normalizeWheelValues(values, sectorNames.length));
  const [errors, setErrors] = useState<boolean[]>([]);

  const isPsyView = !isClient && !inJournal;
  const breakpoint = useBreakpoint();
  const useCompactPsyLayout = isPsyView && breakpoint < Breakpoint.Xl;
  const canConfigure = isPsyView && allowSettings;
  const isMobileForm = formFactor === 'mobile-portrait' || formFactor === 'mobile-landscape';
  const useScrollableStack = useCompactPsyLayout || isMobileForm;
  const lastSyncedConfigRef = useRef<WheelConfig | null>(null);
  const syncTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const committedNames = useMemo(() => normalizeSectorNames(sectorNames, sectorNames.length), [sectorNames]);
  const displayNames = showSettings && canConfigure ? normalizeSectorNames(tempNames, tempCount) : committedNames;
  const displayValues =
    showSettings && canConfigure
      ? normalizeWheelValues(tempValues, tempCount)
      : normalizeWheelValues(localValues ?? values, committedNames.length);
  const lifeItems = useMemo(() => buildLifeItemsFromNames(displayNames), [displayNames]);
  const wheelValues = displayValues;

  const emitConfigChange = useCallback(
    (config: WheelConfig, options?: { immediate?: boolean }) => {
      if (!onConfigChange) return;
      const normalized = normalizeWheelConfig(config.values, config.sectorNames);
      if (lastSyncedConfigRef.current && areWheelConfigsEqual(lastSyncedConfigRef.current, normalized)) {
        return;
      }

      const publish = () => {
        lastSyncedConfigRef.current = normalized;
        onConfigChange(normalized);
      };

      if (options?.immediate) {
        if (syncTimerRef.current) {
          clearTimeout(syncTimerRef.current);
          syncTimerRef.current = null;
        }
        publish();
        return;
      }

      if (syncTimerRef.current) {
        clearTimeout(syncTimerRef.current);
      }
      syncTimerRef.current = setTimeout(publish, CONFIG_SYNC_THROTTLE_MS);
    },
    [onConfigChange],
  );

  useEffect(() => {
    return () => {
      if (syncTimerRef.current) {
        clearTimeout(syncTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!sectorNamesProp && !inJournal) return;
    const count = sectorNamesProp?.length || values.length || DEFAULT_SECTOR_NAMES.length;
    const next = normalizeSectorNames(sectorNamesProp ?? DEFAULT_SECTOR_NAMES, count);
    setSectorNames((prev) => (prev.length === next.length && prev.every((name, i) => name === next[i]) ? prev : next));
  }, [sectorNamesProp, values, inJournal]);

  useEffect(() => {
    setLocalValues(null);
  }, [values]);

  useEffect(() => {
    if (!canConfigure) {
      setShowSettings(false);
    }
  }, [canConfigure]);

  useEffect(() => {
    onSettingsVisibilityChange?.(showSettings);
  }, [showSettings, onSettingsVisibilityChange]);

  useEffect(() => {
    onFullscreenChange?.(isFullscreen);
  }, [isFullscreen, onFullscreenChange]);

  useEffect(() => {
    if (!canConfigure || !showSettings) return;
    emitConfigChange({
      values: normalizeWheelValues(tempValues, tempCount),
      sectorNames: normalizeSectorNames(tempNames, tempCount),
    });
  }, [canConfigure, showSettings, tempCount, tempNames, tempValues, emitConfigChange]);

  const renderClientSelectingOverlay = () =>
    shouldShowClientOverlay ? (
      <div className="absolute inset-0 z-30 pointer-events-none flex items-center justify-center">
        <div
          className="px-2 py-2 rounded-control bg-ink-subtle/60 text-white text-center text-ui-xs-strong md:text-ui-strong uppercase tracking-wide shadow-overlay animate-pulse"
          style={{ animationDuration: '5s', transform: 'translateY(-20px)' }}
        >
          Клиент выбирает
        </div>
      </div>
    ) : null;

  const renderClientStatusOverlay = () =>
    shouldShowClientStatus ? (
      <div className="absolute inset-0 z-30 pointer-events-none flex items-center justify-center">
        <div className="rounded-control bg-ink-subtle/80 px-4 py-3 text-center text-ui-strong text-white shadow-overlay">
          {clientStatusMessage}
        </div>
      </div>
    ) : null;

  useEffect(() => {
    if (isClient) {
      setEditMode(Boolean(setValues && selectionUnlocked));
    }
  }, [isClient, selectionUnlocked, setValues]);

  const onReady = () => {
    if (setReady && isClient) {
      setReady();
    }
    setEditMode(false);
  };

  const onChange = (result: number[]) => {
    if (setValues && editMode) {
      setValues(result);
    }
  };

  const openSettings = useCallback(() => {
    setTempCount(sectorNames.length);
    setTempNames(
      Array.from(
        { length: sectorNames.length },
        (_, i) => sectorNames[i] ?? DEFAULT_SECTOR_NAMES[i] ?? `Сектор ${i + 1}`,
      ),
    );
    setTempValues(normalizeWheelValues(localValues ?? values, sectorNames.length));
    setErrors([]);
    setShowSettings(true);
  }, [sectorNames, localValues, values]);

  const closeSettings = () => {
    const errs = Array.from({ length: tempCount }, (_, i) => !(tempNames[i] ?? '').trim());
    if (errs.some(Boolean)) {
      setErrors(errs);
      return;
    }

    const nextConfig = normalizeWheelConfig(tempValues, tempNames);
    setSectorNames(nextConfig.sectorNames);
    if (setValues) {
      setValues(nextConfig.values);
    } else {
      setLocalValues(nextConfig.values);
    }
    emitConfigChange(nextConfig, { immediate: true });
    setShowSettings(false);
  };

  const changeCount = (n: number) => {
    const c = clampSectorCount(n);
    setTempCount(c);
    setTempNames((prev) => Array.from({ length: c }, (_, i) => prev[i] ?? `Сектор ${i + 1}`));
    setTempValues((prev) => Array.from({ length: c }, (_, i) => prev[i] ?? 5));
    setErrors([]);
  };

  const removeSector = (index: number) => {
    if (tempCount <= MIN_WHEEL_SECTOR_COUNT) return;
    setTempCount(tempCount - 1);
    setTempNames((prev) => prev.filter((_, i) => i !== index));
    setTempValues((prev) => prev.filter((_, i) => i !== index));
    setErrors((prev) => prev.filter((_, i) => i !== index));
  };

  const applyTemplate = useCallback((names: string[]) => {
    const c = clampSectorCount(names.length);
    const nextNames = normalizeSectorNames(names, c);
    setTempCount(c);
    setTempNames(nextNames);
    setTempValues(Array.from({ length: c }, () => 5));
    setErrors([]);
  }, []);

  const resetTempSettings = () => {
    setTempCount(WHEEL_SECTOR_COUNT);
    setTempNames([...DEFAULT_SECTOR_NAMES]);
    setTempValues(Array.from({ length: WHEEL_SECTOR_COUNT }, () => 5));
    setErrors([]);
  };

  const mainText = '!text-body-xl';

  const modal = (
    <Modal
      isOpen={modalShow}
      tone="inverse"
      title="Колесо жизненного баланса"
      onClose={() => setModalShow(false)}
      footer={
        <Button size="md" variant="neutral" onClick={() => setModalShow(false)}>
          Понятно
        </Button>
      }
    >
      <div className="flex flex-col gap-4 text-body-l text-line-strong">
        <p>Оцените каждую сферу вашей жизни по шкале от 1 до 10, где:</p>
        <div>
          <div>1 - полная неудовлетворенность</div>
          <div>10 - полная удовлетворенность</div>
        </div>
        <p>Нажмите на сегмент, чтобы изменить оценку.</p>
      </div>
    </Modal>
  );

  const titleSize = small
    ? mainText
    : 'text-body-xl xl:text-display-m xl:font-normal 2xl:text-display-l largePC:text-display-xl largePC:font-normal';
  const numberText = inJournal
    ? 'text-ui-xs/2 md:text-ui/2 lg:text-body-l'
    : small
      ? 'text-ui-xs/2 md:text-ui/2 lg:text-body-l xl:text-body-xl 2xl:text-heading-lg 2xl:font-normal largePC:text-display-s largePC:font-normal'
      : mainText;

  return (
    <div
      className={
        'relative box-border h-full w-full max-w-full min-w-0 min-h-0 flex flex-col rounded-control bg-surface-muted items-center overflow-hidden cursor-pointer'
      }
    >
      <Button
        size="sm"
        variant="overlay"
        startIcon={<Maximize size={16} aria-hidden />}
        className={`absolute z-20 ${formFactor === 'mobile-portrait' ? 'bottom-2 left-2' : 'top-2 left-2'}`}
        onClick={() => setIsFullscreen(true)}
      >
        Увеличить
      </Button>

      {canConfigure && isMobileForm && (
        <IconButton
          label="Настройки колеса"
          size="md"
          variant="neutral"
          className="absolute bottom-2 right-2 z-20"
          onClick={openSettings}
        >
          <Settings size={16} />
        </IconButton>
      )}

      {!inJournal && (
        <div
          className={`relative flex-none shrink-0 w-full max-w-full min-w-0 bg-surface-muted px-3 flex items-start justify-between gap-3 ${
            isMobileForm ? 'pt-2 pb-2 pr-28' : 'pt-2'
          }`}
        >
          <div
            className={`min-w-0 break-words font-medium text-ink ${
              isMobileForm ? 'text-body-l leading-snug' : titleSize
            } cursor-pointer`}
          >
            Колесо жизненного баланса
          </div>
          {canConfigure && !isMobileForm && (
            <Button
              type="button"
              size="md"
              aria-label="Настройки колеса"
              startIcon={<Settings size={15} />}
              onClick={openSettings}
            >
              <span className="hidden sm:inline">Настройки колеса</span>
            </Button>
          )}
        </div>
      )}

      <div
        className={`relative flex w-full max-w-full min-w-0 min-h-0 flex-1 flex-col ${
          !inJournal && !useScrollableStack ? 'overflow-visible' : 'overflow-x-hidden overflow-y-auto'
        }`}
      >
        {inJournal ? (
          <div className={`w-full h-full p-2 ${formFactor === 'mobile-portrait' ? 'pb-8' : 'pt-12'}`}>
            <div className="grid grid-cols-2 gap-3 font-medium text-ink">
              {lifeItems.map((item, idx) => {
                const bg = item.color;
                const fg = getContrastColorForHex(bg);
                const val = values[idx] === 0 ? '' : String(values[idx]);
                return (
                  <div key={`journal-title-${idx}`} className="flex items-center gap-2 min-w-0">
                    <span
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '24px',
                        height: '24px',
                        minWidth: '24px',
                        minHeight: '24px',
                        borderRadius: '50%',
                        backgroundColor: bg,
                        color: fg,
                        fontSize: '14px',
                        fontWeight: 700,
                        lineHeight: '24px',
                        textAlign: 'center',
                        boxShadow: '0 0 0 1px rgba(0,0,0,0.1) inset',
                      }}
                    >
                      {val}
                    </span>
                    <span className="min-w-0 break-words">{item.title}</span>
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          <div
            className={`flex w-full max-w-full min-w-0 box-border p-2 gap-4 ${
              isPsyView ? 'pt-1 max-[699px]:pb-8' : isMobileForm ? 'pt-2 pb-8' : 'pt-1'
            } ${useScrollableStack ? 'flex-col h-auto' : 'flex-1 h-full flex-col xl:flex-row min-h-0'}`}
          >
            <div
              className={`flex min-w-0 max-w-full flex-col font-medium ${numberText} text-ink ${
                useScrollableStack ? 'w-full flex-none' : 'flex-1 min-h-0'
              }`}
            >
              {!isMobileForm && (
                <div className="flex items-center justify-between mb-1 px-1">
                  <div className="flex items-center gap-4 text-ui font-normal text-ink-muted">
                    <span className="flex items-center gap-1.5">
                      <span className="inline-block w-2 h-2 rounded-full bg-primary" />
                      Секторов: <strong>{displayNames.length}</strong>
                    </span>
                  </div>
                  {canConfigure && (
                    <IconButton label="Настройки колеса" variant="neutral" onClick={openSettings}>
                      <Settings size={16} />
                    </IconButton>
                  )}
                </div>
              )}
              <div
                className={`relative flex w-full min-w-0 ${
                  isMobileForm
                    ? 'flex-none flex-col overflow-visible pb-14'
                    : useCompactPsyLayout
                      ? 'mx-auto aspect-square max-w-[min(100%,72vh,640px)] flex-none items-center justify-center overflow-visible'
                      : 'flex-1 min-h-0 items-center justify-center overflow-visible'
                }`}
              >
                <WheelOfBalance
                  lifeItems={lifeItems}
                  values={wheelValues}
                  saveResult={editMode ? onChange : undefined}
                  disableHover
                  expanded={!isClient}
                  clientMobileLayout={isClient}
                  sideListLabels={withTips}
                />
                {renderClientSelectingOverlay()}
                {renderClientStatusOverlay()}
              </div>
            </div>
          </div>
        )}
      </div>

      {editMode && (
        <Button size="md" variant="primary" className="my-2 min-w-[100px]" onClick={() => onReady()}>
          Готово
        </Button>
      )}
      {!isClient && onButtonTips !== undefined && (
        <Button size="md" variant="primary" className="my-4 max-w-full sm:min-w-[200px]" onClick={() => onButtonTips()}>
          {withTips ? 'Закрыть подсказки' : 'Подсказки'}
        </Button>
      )}
      {isClient && modal}

      {showSettings && (
        <WheelSettingsPanel
          tempCount={tempCount}
          tempNames={tempNames}
          errors={errors}
          userId={userData?.id}
          onClose={closeSettings}
          onChangeCount={changeCount}
          onChangeName={(index, name) => {
            const next = [...tempNames];
            next[index] = name.slice(0, MAX_SECTOR_NAME_LENGTH);
            setTempNames(next);
            if (errors[index]) {
              const errs = [...errors];
              errs[index] = false;
              setErrors(errs);
            }
          }}
          onRemoveSector={removeSector}
          onReset={resetTempSettings}
          onApplyTemplate={applyTemplate}
        />
      )}

      {isFullscreen &&
        createPortal(
          <div className="fixed inset-0 z-panel bg-black/80">
            <div className="flex items-center justify-between p-2">
              <div className="flex-1 flex justify-start">
                <Button
                  size="sm"
                  variant="overlay"
                  startIcon={<Minimize size={16} aria-hidden />}
                  onClick={() => setIsFullscreen(false)}
                >
                  Уменьшить
                </Button>
              </div>
              <div className="flex-1 flex justify-end gap-2">
                {canConfigure && (
                  <Button type="button" size="md" startIcon={<Settings size={15} />} onClick={openSettings}>
                    Настройки
                  </Button>
                )}
                {editMode && (
                  <Button
                    size="md"
                    variant="primary"
                    className="min-w-[100px]"
                    onClick={() => {
                      onReady();
                      setIsFullscreen(false);
                    }}
                  >
                    Готово
                  </Button>
                )}
              </div>
            </div>
            <div className="box-border flex h-[calc(100%-3rem)] max-h-[calc(100vh-3rem)] w-full items-center justify-center px-2 pb-2">
              <div className="relative h-full w-full rounded-card bg-white p-2">
                <div className="relative flex h-full w-full items-center justify-center">
                  <WheelOfBalance
                    lifeItems={lifeItems}
                    values={wheelValues}
                    fullscreen={true}
                    saveResult={editMode ? onChange : undefined}
                    disableHover
                    clientMobileLayout={isClient}
                  />
                  {renderClientSelectingOverlay()}
                  {renderClientStatusOverlay()}
                </div>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
};
